// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { SetStateAction, useCallback, useState } from 'react';
import { readLocalStorageValue } from 'common/helpers/readLocalStorageValue';

interface UseLocalStorage {
    <V>(key: string, initialValue: V): [V, (value: SetStateAction<V>) => void];
    <V = undefined>(key: string): [V | undefined, (value: SetStateAction<V | undefined>) => void];
}

export const useLocalStorage: UseLocalStorage = function <V> (
    key: string,
    initialValue?: V | undefined,
) {
    const [storedValue, setStoredValue] = useState<V | undefined>(
        () => readLocalStorageValue(key, initialValue),
    );

    const setValue = useCallback((value: SetStateAction<V | undefined>) => {
        setStoredValue(prev => {
            const newValue = value instanceof Function ? value(prev) : value;
            if (newValue === undefined) {
                window.localStorage.removeItem(key);
            } else {
                window.localStorage.setItem(key, JSON.stringify(newValue));
            }
            return newValue;
        });
    }, [key]);

    return [storedValue, setValue] as [V, (value: SetStateAction<V>) => void];
};

export default useLocalStorage;
